import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, ChevronRight, RefreshCw, Truck } from "lucide-react";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";

import * as fbs from "@/api/fbs";
import { Button } from "@/components/ui/button";
import {
  acceptanceLabel,
  boxesWord,
  deadlineLeft,
  deadlineUrgent,
  piecesWord,
  supplyStatusLabel,
  useSyncOrders,
} from "@/lib/fbs";
import { AccessDenied, isForbidden, LoadState } from "@/lib/scan-ui/screen";

/**
 * S0 «ФБС» — вход в работу.
 *
 * Волны как сущности нет: единица работы — ПОСТАВКА, одна открытая на юрлицо.
 * Поэтому экран — это плитки юрлиц («что можно собирать прямо сейчас» + «что
 * с открытой поставкой») и вход в завоз комнаты.
 *
 * Тап по плитке — ПРОСТО навигация на сбор, без единого запроса: поставку в WB
 * заводит первый скан. Раньше здесь висел `openSupply`, и кладовщик, который
 * заглянул на экран и ушёл, оставлял в WB пустую поставку.
 */
/** Ссылка на список перемещений с УЖЕ выставленными фильтрами завоза.
 *
 *  Организация-владелец, склад-источник и склад ФБС каждый раз одни и те же;
 *  выставлять их руками — лишний шаг, а промахнувшись, кладовщик не находит
 *  свой заказ и заводит второй. Конфиг ещё не приехал — идём без фильтров,
 *  список просто откроется как раньше.
 */
function transferCheckLink(config?: fbs.FbsConfig): string {
  if (!config) return "/transfer-check";
  const qs = new URLSearchParams();
  if (config.transfer_org_ref) qs.set("org", config.transfer_org_ref);
  if (config.transfer_from_ref) qs.set("from", config.transfer_from_ref);
  if (config.transfer_to_ref) qs.set("to", config.transfer_to_ref);
  const tail = qs.toString();
  return tail ? `/transfer-check?${tail}` : "/transfer-check";
}

export function FbsHomePage(): React.ReactElement {
  const navigate = useNavigate();
  const configQ = useQuery({ queryKey: ["fbs-config"], queryFn: fbs.getConfig });

  const overviewQ = useQuery({
    queryKey: ["fbs-overview"],
    queryFn: fbs.getOverview,
  });
  const overview = overviewQ.data;

  const syncMut = useSyncOrders();

  const cabinets = overview?.cabinets ?? [];

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-2">
        <div>
          <h1 className="text-2xl font-semibold">ФБС</h1>
          <p className="text-sm text-muted-foreground">
            {overview
              ? `Доступно к сбору: ${overview.collectable} ${piecesWord(overview.collectable)} · собрано ${overview.picked}`
              : "Wildberries FBS"}
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => syncMut.mutate()}
          disabled={syncMut.isPending}
        >
          <RefreshCw
            className={`mr-1 size-4 ${syncMut.isPending ? "animate-spin" : ""}`}
          />
          Обновить заказы
        </Button>
      </div>

      {/* Ошибка сводки не должна прятать переходы: завоз в комнату идёт по
          всем юрлицам и остаётся доступным, даже если WB сейчас не отвечает. */}
      {overviewQ.isError && isForbidden(overviewQ.error) ? (
        // 403 — это «вы не привязаны к точке сборки» или «раздел вам не выдан»:
        // причину сервер называет словами, и повторять запрос незачем.
        <AccessDenied
          error={overviewQ.error}
          className="flex flex-col items-center justify-center gap-3 rounded-xl border border-amber-300 bg-amber-50 p-6 text-center"
        />
      ) : (
        (overviewQ.isLoading || overviewQ.isError) && (
          <LoadState
            isLoading={overviewQ.isLoading}
            error={overviewQ.error}
            onRetry={() => overviewQ.refetch()}
            className="flex flex-col items-center justify-center gap-3 rounded-xl border p-6 text-center"
          />
        )
      )}

      {overviewQ.isSuccess && cabinets.length === 0 && (
        <p className="rounded-lg border border-dashed p-4 text-sm text-muted-foreground">
          Кабинеты WB не настроены. Заведите их в разделе «ФБС» системных
          настроек — без токена и склада продавца заказы не подтянутся.
        </p>
      )}

      <div className="grid gap-3 sm:grid-cols-2">
        {cabinets.map((cabinet) => (
          <CabinetTile
            key={cabinet.cabinet_id}
            cabinet={cabinet}
            onOpen={(wh, b2b) => {
              const qs = new URLSearchParams();
              if (wh) qs.set("wh", String(wh));
              if (b2b) qs.set("b2b", "1");
              const tail = qs.toString();
              navigate(
                `/fbs/cabinets/${cabinet.cabinet_id}/pick${tail ? `?${tail}` : ""}`,
              );
            }}
            onFinish={(supplyUuid) =>
              navigate(`/fbs/supplies/${supplyUuid}/finish`)
            }
            onSupplies={() =>
              navigate(`/fbs/cabinets/${cabinet.cabinet_id}/supplies`)
            }
          />
        ))}
      </div>

      {/* Завоз в комнату: портал формирует заказ на перемещение, выполняет
          его «Проверка перемещений». Собственного добора у ФБС нет — он делал
          ту же работу вторым способом и мимо учёта. */}
      <div className="space-y-2">
        <Button
          variant="outline"
          className="h-12 w-full"
          onClick={() => navigate("/fbs/transfer-orders")}
        >
          <Truck className="mr-2 size-4" /> Заказать завоз в комнату
        </Button>
        <Button
          variant="ghost"
          className="h-10 w-full"
          onClick={() => navigate(transferCheckLink(configQ.data))}
        >
          Перемещения на склад ФБС
        </Button>
      </div>
    </div>
  );
}

function CabinetTile({
  cabinet,
  onOpen,
  onFinish,
  onSupplies,
}: {
  cabinet: fbs.CabinetSummary;
  onOpen: (wbWarehouseId: number | undefined, b2b: boolean) => void;
  onFinish: (supplyUuid: string) => void;
  onSupplies: () => void;
}): React.ReactElement {
  const urgent = deadlineUrgent(cabinet.nearest_deadline);
  const left = cabinet.collectable;
  // Идти на сбор, когда собирать нечего, незачем: любой скан там упрётся в
  // «заданий больше нет». Сначала «Обновить заказы».
  const nothingToDo = left === 0 && !cabinet.supply_uuid;
  // Со складом ОДИН вход прежний — тапом по всей плитке. Складов несколько —
  // вход только через строку конкретного склада: два склада WB могут смотреть
  // на одну комнату 1С, и тогда кладовщик стоит среди одного и того же товара.
  // Перепутать, чью поставку он собирает, не должно быть возможности, поэтому
  // склад выбирается ЗДЕСЬ, рядом с юрлицом, а не молча внутри экрана сбора.
  const single = cabinet.warehouses.length <= 1;
  // Тип продажи выбирается ДО входа в сбор: B2B-задания уезжают ОТДЕЛЬНОЙ
  // поставкой (WB не даёт смешивать), а грузоместо принадлежит поставке —
  // две открытые означали бы две стопки коробов и штуку не в той коробке.
  // Пока поставка открыта, её тип и определяет режим: переключать нечего.
  const openB2b = cabinet.warehouses.find((w) => w.supply_id)?.supply_is_b2b;
  const [b2b, setB2b] = useState(false);
  const mode = openB2b ?? b2b;

  const supplyChip = (wh: fbs.CabinetWarehouseSupply) => (
    <span
      className={
        wh.supply_id
          ? "shrink-0 rounded bg-emerald-100 px-2 py-0.5 text-emerald-800"
          : "shrink-0 rounded bg-slate-100 px-2 py-0.5 text-slate-700"
      }
    >
      {wh.supply_id
        ? `${wh.supply_id} · ${supplyStatusLabel(wh.supply_status)} · ${wh.open_boxes} ${boxesWord(wh.open_boxes)}`
        : "поставка не создана"}
    </span>
  );

  const summary = (
    <>
      <div className="flex items-center justify-between">
        <span className="text-lg font-semibold">{cabinet.name}</span>
        <div className="flex items-center gap-2">
          {/* ⚠️ Песочница обязана быть ВИДНА: боевой токен на песочном адресе
              даёт 403, и по нему не догадаться, что дело в галке кабинета.
              Двух одинаковых по имени кабинетов на экране это тоже
              различает. */}
          {cabinet.wb_sandbox && (
            <span className="rounded bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-900">
              песочница
            </span>
          )}
          {single && <ChevronRight className="size-5 text-muted-foreground" />}
        </div>
      </div>

      <div className="mt-1 text-sm">
        {nothingToDo ? (
          "Новых заданий нет"
        ) : (
          <>
            Доступно к сбору: <b>{left}</b> {piecesWord(left)} ·{" "}
            {cabinet.articles_pending} артикул(ов)
            {cabinet.deferred > 0 && (
              <span className="text-muted-foreground">
                {" "}
                · отложено {cabinet.deferred}
              </span>
            )}
          </>
        )}
      </div>

      <div
        className={`mt-1 text-xs ${urgent ? "font-medium text-rose-600" : "text-muted-foreground"}`}
      >
        Ближайший дедлайн: {deadlineLeft(cabinet.nearest_deadline)}
      </div>

      <div className="mt-1 text-xs text-muted-foreground">
        Приёмка сегодня: {acceptanceLabel(cabinet)}
      </div>

      <div className="mt-2 flex flex-wrap gap-1 text-xs">
        {single &&
          cabinet.warehouses.map((wh) => (
            <React.Fragment key={wh.wb_warehouse_id}>
              {supplyChip(wh)}
            </React.Fragment>
          ))}
        {cabinet.zero_orders > 0 && (
          <span className="rounded bg-amber-100 px-2 py-0.5 text-amber-800">
            нулевой остаток: {cabinet.zero_orders}
          </span>
        )}
      </div>

      {cabinet.error && (
        <p className="mt-2 flex items-start gap-1 text-xs text-rose-600">
          <AlertTriangle className="mt-0.5 size-3 shrink-0" />
          {cabinet.error}
        </p>
      )}
    </>
  );

  return (
    <div className="rounded-xl border">
      {/* Выбор типа продажи. Счётчики честные: сколько заданий какого типа
          ждёт. Открытая поставка фиксирует тип — тогда кнопки только
          показывают, в каком режиме идёт работа. */}
      <div className="grid grid-cols-2 gap-2 px-4 pb-2 text-xs">
        <Button
          variant={mode ? "outline" : "default"}
          className="h-10"
          disabled={openB2b === true || cabinet.collectable_b2c === 0}
          onClick={() => setB2b(false)}
        >
          Обычные · {cabinet.collectable_b2c}
        </Button>
        <Button
          variant={mode ? "default" : "outline"}
          className="h-10"
          disabled={openB2b === false || cabinet.collectable_b2b === 0}
          onClick={() => setB2b(true)}
        >
          B2B · {cabinet.collectable_b2b}
        </Button>
      </div>

      {single ? (
        <button
          type="button"
          onClick={() => onOpen(cabinet.warehouses[0]?.wb_warehouse_id, mode)}
          disabled={nothingToDo}
          className="w-full p-4 text-left transition hover:border-primary disabled:opacity-60"
        >
          {summary}
        </button>
      ) : (
        <>
          <div className="p-4">{summary}</div>
          <div className="flex flex-col gap-1 px-4 pb-3 text-xs">
            {cabinet.warehouses.map((wh) => (
              <button
                key={wh.wb_warehouse_id}
                type="button"
                onClick={() => onOpen(wh.wb_warehouse_id, mode)}
                disabled={nothingToDo}
                className="flex items-center justify-between gap-2 rounded border px-2 py-2 text-left transition hover:border-primary disabled:opacity-60"
              >
                <span className="truncate font-semibold">
                  {wh.name || `Склад ${wh.wb_warehouse_id}`}
                </span>
                <span className="flex shrink-0 items-center gap-1">
                  {supplyChip(wh)}
                  <ChevronRight className="size-4 text-muted-foreground" />
                </span>
              </button>
            ))}
          </div>
        </>
      )}

      {/* ⚠️ Вход в список поставок есть ВСЕГДА, даже когда открытой нет:
          именно тогда он и нужен — переданная в доставку уходит с этого
          экрана, а доделать по ней остаются «Разбор» и документы 1С. */}
      <div className="border-t px-4 py-2">
        <Button
          variant="ghost"
          size="sm"
          className="w-full justify-between"
          onClick={onSupplies}
        >
          Поставки кабинета
          <ChevronRight className="size-4" />
        </Button>
      </div>

      {/* Кнопка на каждую открытую поставку: иначе поставка второго склада
          недостижима — экран завершения единственный путь к передаче в
          доставку и документам. */}
      {cabinet.warehouses
        .filter((wh) => wh.supply_uuid)
        .map((wh) => (
          <div key={wh.wb_warehouse_id} className="border-t px-4 py-2">
            <Button
              variant="ghost"
              size="sm"
              className="w-full justify-between"
              onClick={() => onFinish(wh.supply_uuid as string)}
            >
              Завершение поставки
              {!single && ` · ${wh.name || wh.wb_warehouse_id}`}
              <ChevronRight className="size-4" />
            </Button>
          </div>
        ))}
    </div>
  );
}
